/**
 * OutreachHourZones (KAN-664)
 *
 * 24 hover/focus zones over the day bar of the "Рассылка сегодня" block, one
 * per hour, each with a tooltip: who got a first message in that hour and
 * who is planned for it. Buckets come from `lib/outreachTimeline.hourBuckets`;
 * this file only renders them. The tooltip is portaled (the bar clips
 * overflow) and Radix keeps it inside the viewport on the edge hours.
 * KAN-679: the zone itself stays transparent; the hovered/focused hour is
 * reported to the parent (`onActiveHourChange`), which draws the highlight
 * on the bar under the sent ticks and the caption under the bar.
 */
import type { HourBucket } from '@/lib/outreachTimeline'
import { hourRangeLabel, hourToPercent } from '@/lib/outreachTimeline'
import { Tooltip, TooltipContent, TooltipPortal, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

/** Rows shown per block before "и ещё K". */
export const TOOLTIP_ROWS = 10

interface Props {
  buckets: HourBucket[]
  /** KAN-679: hour under the pointer or keyboard focus (null when none). */
  activeHour?: number | null
  onActiveHourChange?: (hour: number | null) => void
}

function More({ count }: { count: number }) {
  if (count <= 0) return null
  return <li className="text-gray-500">и ещё {count}</li>
}

function SentBlock({ bucket }: { bucket: HourBucket }) {
  const rows = bucket.sent.slice(0, TOOLTIP_ROWS)
  return (
    <div data-testid="hour-sent">
      <div className="font-medium">Отправлено ({bucket.sent.length})</div>
      {bucket.sent.length === 0 ? (
        <div className="text-gray-500">нет отправок</div>
      ) : (
        <ul>
          {rows.map((row) => (
            <li key={`${row.at}:${row.recipient}`}>
              {row.time} {row.recipient}, {row.label}
            </li>
          ))}
          <More count={bucket.sent.length - rows.length} />
        </ul>
      )}
    </div>
  )
}

function PlannedBlock({ bucket }: { bucket: HourBucket }) {
  const rows = bucket.planned.slice(0, TOOLTIP_ROWS)
  return (
    <div data-testid="hour-planned">
      <div className="font-medium">По расписанию (оценка, {bucket.planned.length})</div>
      <ul>
        {rows.map((row) => (
          <li key={row.record_id}>
            {row.name || row.phone || 'получатель'}, {row.campaign}
          </li>
        ))}
        <More count={bucket.planned.length - rows.length} />
      </ul>
    </div>
  )
}

export function HourTooltipBody({ bucket }: { bucket: HourBucket }) {
  const showSent = bucket.kind !== 'future' && (bucket.inWindow || bucket.sent.length > 0)
  const showPlanned = bucket.planned.length > 0
  const queueEmpty = bucket.kind === 'future' && bucket.inWindow && bucket.planned.length === 0
  return (
    <div data-testid="hour-tooltip" className="space-y-1 text-xs">
      <div className="font-semibold">{hourRangeLabel(bucket.hour)}</div>
      {!bucket.inWindow && <div className="text-gray-500">Вне окна рассылки</div>}
      {showSent && <SentBlock bucket={bucket} />}
      {showPlanned && <PlannedBlock bucket={bucket} />}
      {queueEmpty && <div className="text-gray-500">Очередь пуста</div>}
    </div>
  )
}

export function OutreachHourZones({ buckets, activeHour = null, onActiveHourChange }: Props) {
  const enter = (hour: number) => () => onActiveHourChange?.(hour)
  const leave = (hour: number) => () => {
    if (activeHour === hour) onActiveHourChange?.(null)
  }
  return (
    <TooltipProvider delayDuration={100} skipDelayDuration={300}>
      {buckets.map((bucket) => (
        <Tooltip key={bucket.hour}>
          <TooltipTrigger asChild>
            <button
              type="button"
              data-testid="outreach-hour-zone"
              data-active={activeHour === bucket.hour ? 'true' : undefined}
              aria-label={`Час ${hourRangeLabel(bucket.hour)}`}
              className="absolute inset-y-0 z-[4] cursor-default border-0 bg-transparent p-0 outline-none"
              style={{ left: `${hourToPercent(bucket.hour)}%`, width: `${100 / 24}%` }}
              onPointerEnter={enter(bucket.hour)}
              onPointerLeave={leave(bucket.hour)}
              onFocus={enter(bucket.hour)}
              onBlur={leave(bucket.hour)}
            />
          </TooltipTrigger>
          <TooltipPortal>
            <TooltipContent
              side="top"
              align="center"
              sideOffset={6}
              collisionPadding={8}
              avoidCollisions
              className="max-w-xs"
            >
              <HourTooltipBody bucket={bucket} />
            </TooltipContent>
          </TooltipPortal>
        </Tooltip>
      ))}
    </TooltipProvider>
  )
}
